import React, { useState, useMemo } from 'react';
import Icon from '../UI/Icon';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import './BookSelector.css';

// Book sections for Old Testament
const OT_SECTIONS = {
  'Pentateuch': ['Gen', 'Exod', 'Lev', 'Num', 'Deut'],
  'History': ['Josh', 'Judg', 'Ruth', '1Sam', '2Sam', '1Kgs', '2Kgs', '1Chr', '2Chr', 'Ezra', 'Neh', 'Esth'],
  'Poetry & Wisdom': ['Job', 'Ps', 'Prov', 'Eccl', 'Song'],
  'Major Prophets': ['Isa', 'Jer', 'Lam', 'Ezek', 'Dan'],
  'Minor Prophets': ['Hos', 'Joel', 'Amos', 'Obad', 'Jonah', 'Mic', 'Nah', 'Hab', 'Zeph', 'Hag', 'Zech', 'Mal']
};

// Book sections for New Testament
const NT_SECTIONS = {
  'Gospels': ['Matt', 'Mark', 'Luke', 'John'],
  'Church History': ['Acts'],
  'Pauline Epistles': ['Rom', '1Cor', '2Cor', 'Gal', 'Eph', 'Phil', 'Col', '1Thess', '2Thess', '1Tim', '2Tim', 'Titus', 'Phlm'],
  'General Epistles': ['Heb', 'Jas', '1Pet', '2Pet', '1John', '2John', '3John', 'Jude'],
  'Apocalyptic': ['Rev']
};

// All OT and NT book IDs for testament detection
const ALL_OT_BOOKS = Object.values(OT_SECTIONS).flat();
const ALL_NT_BOOKS = Object.values(NT_SECTIONS).flat();

// NT book name indicators for fallback detection
const NT_BOOK_NAMES = [
  'Matthew', 'Mark', 'Luke', 'John', 'Acts', 'Romans', 'Corinthians', 
  'Galatians', 'Ephesians', 'Philippians', 'Colossians', 'Thessalonians',
  'Timothy', 'Titus', 'Philemon', 'Hebrews', 'James', 'Peter', 'Jude', 'Revelation'
];

// Determine testament based on book ID or testament property
const getTestament = (book) => {
  // If book has testament property from API, use it
  if (book.testament) {
    return book.testament;
  }
  // Otherwise check against known book IDs
  const bookId = book.id || book;
  // Check NT first (more specific)
  if (ALL_NT_BOOKS.some(nt => bookId === nt || bookId.toLowerCase() === nt.toLowerCase())) {
    return 'NT';
  }
  // Then check OT
  if (ALL_OT_BOOKS.some(ot => bookId === ot || bookId.toLowerCase() === ot.toLowerCase())) {
    return 'OT';
  }
  // Fallback: try to determine by name or default to OT (most books are OT)
  const bookName = book.name || '';
  if (NT_BOOK_NAMES.some(name => bookName.includes(name))) {
    return 'NT';
  }
  return 'OT';
};

function BookSelector({ books, chapters, currentBook, currentChapter, onSelectBook, onSelectChapter, onClose }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState('testament'); // 'testament', 'sections', 'books', 'chapters'
  const [selectedTestament, setSelectedTestament] = useState(
    currentBook ? getTestament(currentBook) : 'OT'
  );
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedBook, setSelectedBook] = useState(currentBook);
  
  // Enable swipe-to-close on mobile
  const { ref: swipeRef } = useSwipeToClose(onClose);
  
  // Filter books based on search
  const filteredBooks = useMemo(() => {
    if (!searchQuery) return books;
    const query = searchQuery.toLowerCase();
    return books.filter(book => 
      book.name.toLowerCase().includes(query) ||
      book.id?.toLowerCase().includes(query)
    );
  }, [books, searchQuery]);
  
  // Group books by testament using testament property or book ID matching
  const groupedBooks = useMemo(() => {
    const ot = [];
    const nt = [];
    
    filteredBooks.forEach((book) => {
      const testament = getTestament(book);
      if (testament === 'OT') {
        ot.push(book);
      } else {
        nt.push(book);
      }
    });
    
    return { ot, nt };
  }, [filteredBooks]);

  // Get books for a specific section
  const getBooksForSection = (sectionName, testament) => {
    const sections = testament === 'OT' ? OT_SECTIONS : NT_SECTIONS;
    const sectionBookIds = sections[sectionName] || [];
    const testamentBooks = testament === 'OT' ? groupedBooks.ot : groupedBooks.nt;
    
    return testamentBooks.filter(book => 
      sectionBookIds.some(id => book.id === id || book.id.toLowerCase() === id.toLowerCase())
    );
  };

  const handleTestamentSelect = (testament) => {
    setSelectedTestament(testament);
    setView('sections');
  };

  const handleSectionSelect = (section) => {
    setSelectedSection(section);
    setView('books');
  };
  
  const handleBookSelect = (bookId) => {
    const book = books.find(b => b.id === bookId);
    setSelectedBook(book);
    setView('chapters');
    onSelectBook(bookId);
  };
  
  const handleChapterSelect = (chapterNum) => {
    onSelectChapter(chapterNum);
    onClose();
  };

  const handleBack = () => {
    if (view === 'chapters') {
      setView('books');
    } else if (view === 'books') {
      setView('sections');
    } else if (view === 'sections') {
      setView('testament');
    }
  };
  
  // Generate chapters for selected book
  const bookChapters = useMemo(() => {
    if (!selectedBook) return [];
    return Array.from({ length: selectedBook.chapters || 1 }, (_, i) => ({
      number: i + 1,
      id: `${selectedBook.id}.${i + 1}`
    }));
  }, [selectedBook]);
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="book-selector modal" ref={swipeRef} onClick={e => e.stopPropagation()}>
        <div className="modal-drag-handle" />
        <div className="modal-header">
          <h2 className="modal-title">
            {view === 'testament' && 'Select Testament'}
            {view === 'sections' && (selectedTestament === 'OT' ? 'Old Testament' : 'New Testament')}
            {view === 'books' && selectedSection}
            {view === 'chapters' && (selectedBook?.name || 'Select Chapter')}
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>
            <Icon name="close" size={20} />
          </button>
        </div>

        {/* Search - always visible */}
        <div className="search-container">
          <Icon name="search" size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search books..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              if (e.target.value) setView('search');
              else setView('testament');
            }}
            className="search-input"
          />
          {searchQuery && (
            <button 
              className="btn btn-ghost btn-icon search-clear"
              onClick={() => {
                setSearchQuery('');
                setView('testament');
              }}
            >
              <Icon name="close" size={16} />
            </button>
          )}
        </div>

        {/* Back button */}
        {view !== 'testament' && view !== 'search' && (
          <button className="btn btn-ghost back-button" onClick={handleBack}>
            <Icon name="chevronLeft" size={18} />
            Back
          </button>
        )}
        
        {/* Testament Selection */}
        {view === 'testament' && !searchQuery && (
          <div className="testament-selection">
            <button 
              className="testament-card"
              onClick={() => handleTestamentSelect('OT')}
            >
              <div className="testament-icon">
                <Icon name="book" size={32} />
              </div>
              <div className="testament-info">
                <h3>Old Testament</h3>
                <p>{groupedBooks.ot.length} books • Genesis to Malachi</p>
              </div>
              <Icon name="chevronRight" size={20} />
            </button>
            
            <button 
              className="testament-card"
              onClick={() => handleTestamentSelect('NT')}
            >
              <div className="testament-icon">
                <Icon name="book" size={32} />
              </div>
              <div className="testament-info">
                <h3>New Testament</h3>
                <p>{groupedBooks.nt.length} books • Matthew to Revelation</p>
              </div>
              <Icon name="chevronRight" size={20} />
            </button>
          </div>
        )}

        {/* Section Selection - Old Testament */}
        {view === 'sections' && !searchQuery && selectedTestament === 'OT' && (
          <div className="sections-list">
            <button 
              className="section-card"
              onClick={() => handleSectionSelect('Pentateuch')}
            >
              <div className="section-info">
                <h3>Pentateuch</h3>
                <p>{getBooksForSection('Pentateuch', 'OT').length} books</p>
              </div>
              <Icon name="chevronRight" size={20} />
            </button>
            
            <button 
              className="section-card"
              onClick={() => handleSectionSelect('History')}
            >
              <div className="section-info">
                <h3>History</h3>
                <p>{getBooksForSection('History', 'OT').length} books</p>
              </div>
              <Icon name="chevronRight" size={20} />
            </button>
            
            <button 
              className="section-card"
              onClick={() => handleSectionSelect('Poetry & Wisdom')}
            >
              <div className="section-info">
                <h3>Poetry & Wisdom</h3>
                <p>{getBooksForSection('Poetry & Wisdom', 'OT').length} books</p>
              </div>
              <Icon name="chevronRight" size={20} />
            </button>
            
            <button 
              className="section-card"
              onClick={() => handleSectionSelect('Major Prophets')}
            >
              <div className="section-info">
                <h3>Major Prophets</h3>
                <p>{getBooksForSection('Major Prophets', 'OT').length} books</p>
              </div>
              <Icon name="chevronRight" size={20} />
            </button>
            
            <button 
              className="section-card"
              onClick={() => handleSectionSelect('Minor Prophets')}
            >
              <div className="section-info">
                <h3>Minor Prophets</h3>
                <p>{getBooksForSection('Minor Prophets', 'OT').length} books</p>
              </div>
              <Icon name="chevronRight" size={20} />
            </button>
          </div>
        )}

        {/* Section Selection - New Testament */}
        {view === 'sections' && !searchQuery && selectedTestament === 'NT' && (
          <div className="sections-list">
            <button 
              className="section-card"
              onClick={() => handleSectionSelect('Gospels')}
            >
              <div className="section-info">
                <h3>Gospels</h3>
                <p>{getBooksForSection('Gospels', 'NT').length} books</p>
              </div>
              <Icon name="chevronRight" size={20} />
            </button>
            
            <button 
              className="section-card"
              onClick={() => handleSectionSelect('Church History')}
            >
              <div className="section-info">
                <h3>Church History</h3>
                <p>{getBooksForSection('Church History', 'NT').length} books</p>
              </div>
              <Icon name="chevronRight" size={20} />
            </button>
            
            <button 
              className="section-card"
              onClick={() => handleSectionSelect('Pauline Epistles')}
            >
              <div className="section-info">
                <h3>Pauline Epistles</h3>
                <p>{getBooksForSection('Pauline Epistles', 'NT').length} books</p>
              </div>
              <Icon name="chevronRight" size={20} />
            </button>
            
            <button 
              className="section-card"
              onClick={() => handleSectionSelect('General Epistles')}
            >
              <div className="section-info">
                <h3>General Epistles</h3>
                <p>{getBooksForSection('General Epistles', 'NT').length} books</p>
              </div>
              <Icon name="chevronRight" size={20} />
            </button>
            
            <button 
              className="section-card"
              onClick={() => handleSectionSelect('Apocalyptic')}
            >
              <div className="section-info">
                <h3>Apocalyptic</h3>
                <p>{getBooksForSection('Apocalyptic', 'NT').length} books</p>
              </div>
              <Icon name="chevronRight" size={20} />
            </button>
          </div>
        )}

        {/* Books Grid */}
        {view === 'books' && !searchQuery && (
          <div className="books-container">
            <div className="books-grid">
              {getBooksForSection(selectedSection, selectedTestament).map(book => (
                <button
                  key={book.id}
                  className={`book-item ${book.id === currentBook?.id ? 'active' : ''}`}
                  onClick={() => handleBookSelect(book.id)}
                >
                  <span className="book-name">{book.name}</span>
                  <span className="book-chapters">{book.chapters} ch</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chapters Grid */}
        {view === 'chapters' && !searchQuery && (
          <div className="chapters-container">
            <div className="chapters-grid">
              {bookChapters.map(chapter => (
                <button
                  key={chapter.id}
                  className={`chapter-item ${chapter.number === currentChapter ? 'active' : ''}`}
                  onClick={() => handleChapterSelect(chapter.number)}
                >
                  {chapter.number}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {view === 'search' && searchQuery && filteredBooks.length > 0 && (
          <div className="books-container">
            {groupedBooks.ot.length > 0 && (
              <div className="book-group">
                <h3 className="group-title">Old Testament</h3>
                <div className="books-grid">
                  {groupedBooks.ot.map(book => (
                    <button
                      key={book.id}
                      className={`book-item ${book.id === currentBook?.id ? 'active' : ''}`}
                      onClick={() => handleBookSelect(book.id)}
                    >
                      <span className="book-name">{book.name}</span>
                      <span className="book-chapters">{book.chapters} ch</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {groupedBooks.nt.length > 0 && (
              <div className="book-group">
                <h3 className="group-title">New Testament</h3>
                <div className="books-grid">
                  {groupedBooks.nt.map(book => (
                    <button
                      key={book.id}
                      className={`book-item ${book.id === currentBook?.id ? 'active' : ''}`}
                      onClick={() => handleBookSelect(book.id)}
                    >
                      <span className="book-name">{book.name}</span>
                      <span className="book-chapters">{book.chapters} ch</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {view === 'search' && searchQuery && filteredBooks.length === 0 && (
          <div className="books-container">
            <div className="no-results">
              <p>No books found matching "{searchQuery}"</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BookSelector;
